import classNames from 'classnames';
import React, { useState } from 'react';
import { Container } from 'src/components/layouts/Container/Container';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import TabBar, { TabProps } from 'src/components/layouts/TabBar/TabBar';
import { useTranslation } from 'react-i18next';
import CarrierCard from 'src/components/cardLayouts/CarrierCard/CarrierCard';
import { useSearchParams } from 'react-router-dom';
import styles from './styles/Integrations.module.scss';
import { CarrierCompany, useCarrierQuery } from '../services/carrier';
import useFilters from '../services/filters';
import FilterRow from '../components/layouts/FilterRow/FilterRow';

enum TabKeys {
  ALL = 'all',
  ACTIVE = 'active',
  NON_ACTIVE = 'none_active',
}

const tabLabels: Record<TabKeys, string> = {
  [TabKeys.ALL]: 'carrier_analytics.list_all',
  [TabKeys.ACTIVE]: 'carrier_analytics.list_active',
  [TabKeys.NON_ACTIVE]: 'carrier_analytics.list_non_active',
};

function CarrierList() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabKeys) || TabKeys.ALL;
  const [activeTab, setActiveTab] = useState<TabKeys>(initialTab);

  const handleTabChange = (tabKey: TabKeys) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey });
  };

  const tabs: TabProps[] = [
    {
      label: t(tabLabels[TabKeys.ALL]),
      key: TabKeys.ALL,
      onClick: () => handleTabChange(TabKeys.ALL),
    },
    {
      label: t(tabLabels[TabKeys.ACTIVE]),
      key: TabKeys.ACTIVE,
      onClick: () => handleTabChange(TabKeys.ACTIVE),
    },
    {
      label: t(tabLabels[TabKeys.NON_ACTIVE]),
      key: TabKeys.NON_ACTIVE,
      onClick: () => handleTabChange(TabKeys.NON_ACTIVE),
    },
  ];

  const accessRights = { read_write: true };

  const activeIndex = tabs.findIndex(tab => tab.key === activeTab);
  const filterInstance = useFilters('df.carrier.company');

  const { data: carriers } = useCarrierQuery({
    sort_order: 'asc',
    page: 1,
    domain: filterInstance.domain,
  });

  let data: CarrierCompany[] = [];

  if (activeTab === TabKeys.ALL) {
    data = carriers?.items ?? [];
  } else if (activeTab === TabKeys.ACTIVE) {
    data = carriers?.items?.filter(carrier => carrier.active) ?? [];
  } else if (activeTab === TabKeys.NON_ACTIVE) {
    data = carriers?.items?.filter(carrier => !carrier.active) ?? [];
  }

  return (
    <Container className={classNames(styles.root, 'relative py-32 flex-1')}>
      <div className="flex w-full justify-between items-start">
        <HeadlineWithButtons headline={t('carrier_analytics.carrier_list')} />
      </div>
      <div className="mb-24">
        <TabBar defaultIndex={activeIndex} tabs={tabs} />
        {activeTab === TabKeys.ALL && <div />}
        {activeTab === TabKeys.ACTIVE && <div />}
        {activeTab === TabKeys.NON_ACTIVE && <div />}
      </div>
      <div className="mb-24 w-full">
        <FilterRow
          withSaveFilters={false}
          filterInstance={filterInstance}
          primaryKeys={[{ key: 'custom', operator: '=' }]}
          singleParam
        />
      </div>
      <div className={styles.integrationsGrid}>
        {data?.map(item => <CarrierCard allowEdition={accessRights?.read_write} key={item.id} {...item} />)}
      </div>
    </Container>
  );
}

export default CarrierList;
